import { useState, useEffect, useCallback } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import {
  Monitor, Gear, Circle, Timer as TimerIcon, Camera, ArrowLeft, Keyboard, ShieldWarning
} from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "../components/DateInput";
const STATUS_STYLES = {
  online: { dot: "bg-emerald-500", label: "Online", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  idle: { dot: "bg-amber-500", label: "Idle", badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  offline: { dot: "bg-slate-400", label: "Offline", badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const fmtMins = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${Math.round(m % 60)}m` : `${Math.round(m)}m`;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";

function ScreenshotThumb({ shot, className = "", onClick }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    API.get(`/monitoring/screenshots/${shot.id}/file`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [shot.id]);
  if (!src) return <div className={`bg-muted animate-pulse rounded ${className}`} />;
  return <img src={src} alt="screen capture" className={`rounded object-cover cursor-pointer ${className}`} onClick={onClick} data-testid={`screenshot-thumb-${shot.id}`} />;
}

function SettingsDialog({ open, onOpenChange }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) API.get("/monitoring/settings").then(({ data }) => setS(data)).catch(() => {});
  }, [open]);
  const save = async () => {
    setSaving(true);
    try {
      await API.put("/monitoring/settings", s);
      toast.success("Monitoring settings saved");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="monitoring-settings-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">Monitoring Settings</DialogTitle></DialogHeader>
        {s && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Idle threshold (minutes)</Label>
              <Input type="number" min={1} max={60} value={s.idle_threshold_minutes}
                     onChange={e => setS({ ...s, idle_threshold_minutes: parseInt(e.target.value) || 5 })}
                     className="h-8 w-28" data-testid="idle-threshold-input" />
              <p className="text-[10px] text-muted-foreground mt-1">No keyboard/mouse activity for this long marks the person as idle</p>
            </div>
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div>
                <p className="text-xs font-semibold">Screen snapshots</p>
                <p className="text-[10px] text-muted-foreground">Staff see a prompt to share their screen each session (consent required by the browser)</p>
              </div>
              <Switch checked={s.screenshots_enabled} onCheckedChange={v => setS({ ...s, screenshots_enabled: v })} data-testid="screenshots-enabled-switch" />
            </div>
            {s.screenshots_enabled && (
              <div>
                <Label className="text-xs">Capture interval (minutes)</Label>
                <Input type="number" min={1} max={120} value={s.screenshot_interval_minutes}
                       onChange={e => setS({ ...s, screenshot_interval_minutes: parseInt(e.target.value) || 10 })}
                       className="h-8 w-28" data-testid="screenshot-interval-input" />
              </div>
            )}
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div>
                <p className="text-xs font-semibold">Low activity alerts</p>
                <p className="text-[10px] text-muted-foreground">Notify Operations Directors when someone who was working goes quiet mid-shift (Mon–Fri, 8am–6pm UK)</p>
              </div>
              <Switch checked={s.low_activity_alerts_enabled} onCheckedChange={v => setS({ ...s, low_activity_alerts_enabled: v })} data-testid="low-activity-switch" />
            </div>
            {s.low_activity_alerts_enabled && (
              <div>
                <Label className="text-xs">Alert after (minutes of no activity)</Label>
                <Input type="number" min={10} max={480} value={s.low_activity_alert_minutes}
                       onChange={e => setS({ ...s, low_activity_alert_minutes: parseInt(e.target.value) || 45 })}
                       className="h-8 w-28" data-testid="low-activity-minutes-input" />
              </div>
            )}
            <p className="text-[10px] text-muted-foreground flex items-start gap-1.5">
              <Keyboard size={12} className="mt-0.5 shrink-0" /> Activity tracking counts keyboard and mouse events only — the content of what's typed is never recorded.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="save-monitoring-settings-btn">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UserDetail({ member, onBack }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [fullShot, setFullShot] = useState(null);

  useEffect(() => {
    setData(null);
    API.get(`/monitoring/activity/${member.user_id}?date=${date}`).then(({ data }) => setData(data)).catch(() => {});
  }, [member.user_id, date]);

  const maxActive = Math.max(1, ...((data?.hours || []).map(h => h.active_minutes + h.idle_minutes)));

  return (
    <div data-testid="monitoring-user-detail">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onBack} data-testid="monitoring-back-btn"><ArrowLeft size={16} /></Button>
          <div>
            <h2 className="text-sm font-bold font-[Chivo]">{member.name}</h2>
            <p className="text-[10px] text-muted-foreground">{member.role?.replace("_", " ")} · {member.email}</p>
          </div>
        </div>
        <DateInput value={date} onChange={e => setDate(e.target.value)} className="h-8 w-40 text-xs" data-testid="monitoring-date-input" />
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[["Active time", fmtMins(data.summary.active_minutes)], ["Idle time", fmtMins(data.summary.idle_minutes)],
              ["Avg activity", `${data.summary.avg_activity}%`], ["Timer logged", fmtMins(data.summary.timer_minutes)]].map(([l, v]) => (
              <div key={l} className="border border-border rounded-lg p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                <p className="text-lg font-bold font-mono">{v}</p>
              </div>
            ))}
          </div>

          {/* Hourly activity */}
          <div className="border border-border rounded-lg p-4 mb-5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Hourly Activity</p>
            <div className="flex items-end gap-1 h-24" data-testid="hourly-activity-chart">
              {data.hours.map(h => (
                <div key={h.hour} className="flex-1 flex flex-col justify-end gap-px" title={`${h.hour}:00 — active ${h.active_minutes}m, idle ${h.idle_minutes}m, activity ${h.avg_activity}%`}>
                  <div className="bg-amber-400/70 rounded-t-sm" style={{ height: `${(h.idle_minutes / maxActive) * 100}%` }} />
                  <div className="bg-emerald-500 rounded-t-sm" style={{ height: `${(h.active_minutes / maxActive) * 100}%` }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[9px] text-muted-foreground mt-1"><span>00:00</span><span>12:00</span><span>23:00</span></div>
          </div>

          <div className="grid gap-5 lg:grid-cols-2 mb-5">
            {/* Section usage */}
            <div className="border border-border rounded-lg p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Platform Usage</p>
              {data.sections.length === 0 ? <p className="text-xs text-muted-foreground">No activity recorded</p> : (
                <div className="space-y-2">
                  {data.sections.map(s => (
                    <div key={s.section} className="flex items-center gap-2 text-xs" data-testid={`section-usage-${s.section || "dashboard"}`}>
                      <span className="font-medium flex-1 truncate">{s.label}</span>
                      <Badge className={`text-[9px] ${s.classification === "productive" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400"}`}>
                        {s.classification}
                      </Badge>
                      <span className="font-mono text-muted-foreground w-14 text-right">{fmtMins(s.minutes)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Timers */}
            <div className="border border-border rounded-lg p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">Task Timers</p>
              {data.timers.length === 0 ? <p className="text-xs text-muted-foreground">No timers logged</p> : (
                <div className="space-y-2">
                  {data.timers.map(t => (
                    <div key={t.id} className="flex items-center gap-2 text-xs">
                      <TimerIcon size={12} className="text-primary shrink-0" />
                      <span className="flex-1 truncate">{t.task_title || t.label || t.project_name || "Work"}{t.project_name && t.task_title ? ` · ${t.project_name}` : ""}</span>
                      <span className="text-[10px] text-muted-foreground">{fmtTime(t.started_at)}–{t.ended_at ? fmtTime(t.ended_at) : "now"}</span>
                      <span className="font-mono font-semibold w-12 text-right">{t.ended_at ? fmtMins(t.duration_minutes) : "…"}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Screenshots */}
          <div className="border border-border rounded-lg p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1"><Camera size={12} /> Screen Captures ({data.screenshots.length})</p>
            {data.screenshots.length === 0 ? <p className="text-xs text-muted-foreground">No captures for this day — staff must enable screen sharing in their session</p> : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="screenshots-gallery">
                {data.screenshots.map(s => (
                  <div key={s.id}>
                    <ScreenshotThumb shot={s} className="w-full h-20" onClick={() => setFullShot(s)} />
                    <p className="text-[9px] text-muted-foreground mt-0.5 text-center">{fmtTime(s.ts)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Dialog open={!!fullShot} onOpenChange={v => !v && setFullShot(null)}>
            <DialogContent className="max-w-4xl" data-testid="screenshot-full-dialog">
              <DialogHeader><DialogTitle className="font-[Chivo] text-sm">{member.name} — {fullShot && new Date(fullShot.ts).toLocaleString("en-GB")}</DialogTitle></DialogHeader>
              {fullShot && <ScreenshotThumb shot={fullShot} className="w-full" />}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

export default function StaffMonitoringPage() {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const fetchTeam = useCallback(() => {
    API.get("/monitoring/team").then(({ data }) => setTeam(data.team)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.operations_director) return;
    fetchTeam();
    const iv = setInterval(fetchTeam, 30000);
    return () => clearInterval(iv);
  }, [fetchTeam, user?.operations_director]);

  if (!user?.operations_director) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="monitoring-access-denied">
        <ShieldWarning size={40} className="text-muted-foreground mb-3" />
        <p className="text-sm font-semibold">Operations Director access required</p>
        <p className="text-xs text-muted-foreground mt-1">Ask a super admin to grant you the Operations Director flag in Settings → Users.</p>
      </div>
    );
  }

  if (selected) return <UserDetail member={selected} onBack={() => { setSelected(null); fetchTeam(); }} />;

  const counts = { online: 0, idle: 0, offline: 0 };
  (team || []).forEach(m => { counts[m.status] += 1; });

  return (
    <div data-testid="staff-monitoring-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight flex items-center gap-2"><Monitor size={22} /> Staff Monitoring</h1>
          <p className="text-xs text-muted-foreground mt-0.5">Live presence, in-platform activity and task timers · refreshes every 30s · no keystroke content is recorded</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 text-[11px]">
            <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-emerald-500" /> {counts.online} online</span>
            <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-amber-500" /> {counts.idle} idle</span>
            <span className="flex items-center gap-1"><Circle size={8} weight="fill" className="text-slate-400" /> {counts.offline} offline</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)} data-testid="monitoring-settings-btn">
            <Gear size={14} className="mr-1" /> Settings
          </Button>
        </div>
      </div>

      {!team ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>
      ) : team.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-10 text-center">No monitored staff accounts found.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="monitoring-team-grid">
          {team.map(m => {
            const st = STATUS_STYLES[m.status];
            return (
              <button key={m.user_id} onClick={() => setSelected(m)}
                className="text-left border border-border rounded-lg p-4 hover:border-primary/50 transition-colors bg-card"
                data-testid={`monitor-card-${m.user_id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${st.dot} ${m.status === "online" ? "animate-pulse" : ""}`} />
                    <span className="text-sm font-semibold truncate">{m.name}</span>
                  </div>
                  <Badge className={`text-[9px] shrink-0 ${st.badge}`}>{st.label}</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground mb-2.5">{m.role?.replace("_", " ")}{m.current_section ? ` · viewing ${m.current_section}` : m.last_seen ? ` · last seen ${fmtTime(m.last_seen)}` : ""}</p>
                <div className="flex items-center gap-3 text-[11px] mb-2">
                  <span>Active <span className="font-mono font-semibold">{fmtMins(m.active_minutes)}</span></span>
                  <span className="text-muted-foreground">Idle <span className="font-mono">{fmtMins(m.idle_minutes)}</span></span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-2" title={`Average activity ${m.avg_activity}%`}>
                  <div className={`h-full ${m.avg_activity >= 50 ? "bg-emerald-500" : m.avg_activity >= 20 ? "bg-amber-500" : "bg-slate-400"}`} style={{ width: `${m.avg_activity}%` }} />
                </div>
                <div className="flex items-center justify-between">
                  {m.running_timer ? (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium truncate">
                      <TimerIcon size={11} weight="fill" /> {m.running_timer.task_title || m.running_timer.label || m.running_timer.project_name || "Working"}
                    </span>
                  ) : <span className="text-[10px] text-muted-foreground">No timer running</span>}
                  {m.last_screenshot && <Camera size={12} className="text-muted-foreground shrink-0" title={`Last capture ${fmtTime(m.last_screenshot.ts)}`} />}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} />
    </div>
  );
}
